import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, takeUntil, filter } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-auth-redirect',
  standalone: true,
  template: ''
})
export class AuthRedirectComponent implements OnInit, OnDestroy {
  private msal = inject(MsalService);
  private msalBroadcast = inject(MsalBroadcastService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    console.log('[AuthRedirectComponent] ngOnInit, current URL:', this.router.url);
    // Wait for MSAL to finish any redirect or popup interaction
    this.msalBroadcast.inProgress$
      .pipe(
        filter(status => status === InteractionStatus.None),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        const accounts = this.msal.instance.getAllAccounts();
        console.log('[AuthRedirectComponent] MSAL accounts:', accounts);
        if (accounts.length > 0) {
          this.msal.instance.setActiveAccount(accounts[0]);
          void this.navigateForAuthenticatedUser();
          return;
        } else {
          console.log('[AuthRedirectComponent] Navigating to /public');
          this.router.navigate(['/public'], { replaceUrl: true });
        }
      });
  }

  private async navigateForAuthenticatedUser(): Promise<void> {
    const pendingInvitationToken = sessionStorage.getItem('pendingInvitationToken');
    if (pendingInvitationToken) {
      sessionStorage.removeItem('pendingInvitationToken');
      this.router.navigate(['/accept-invitation'], {
        queryParams: { token: pendingInvitationToken, verified: '1' },
        replaceUrl: true
      });
      return;
    }

    const pendingLegalRoute = await this.authService.getPendingLegalRouteAsync(true);
    if (pendingLegalRoute) {
      console.log(`[AuthRedirectComponent] Navigating to pending legal route ${pendingLegalRoute}`);
      this.router.navigate([pendingLegalRoute], { replaceUrl: true });
      return;
    }

    const roles = await this.authService.getRolesAsync();
    const isTeamMember = roles.includes('TeamMember');
    const targetRoute = isTeamMember ? '/my-media' : '/home';

    if (this.router.url === targetRoute) {
      console.log(`[AuthRedirectComponent] Already on ${targetRoute}, forcing reload`);
      this.router.navigateByUrl('/public', { skipLocationChange: true }).then(() => {
        console.log(`[AuthRedirectComponent] Navigating to ${targetRoute} (forced)`);
        this.router.navigate([targetRoute], { replaceUrl: true });
      });
      return;
    }

    console.log(`[AuthRedirectComponent] Navigating to ${targetRoute}`);
    this.router.navigate([targetRoute], { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
